import type { StateAction } from "@entrilia/shared/types";
import { distinct, distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type { Validator } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  emailValidator,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import type { Fund } from "../../../../../api/types/commonApiTypes";
import type { FundInvestorContact, InvestorPortalRole } from "../../../../../api/types/investorContactsTypes";
import type {
  MemberFundPermissions,
  MembersChangeRequestView,
  UpsertMembersChangeRequestRequest,
} from "../../../../../api/types/membersChangeRequestTypes";
import type { InvestorFundPermissionSet } from "../../../../../store/state/user/types";
import type { NewContactChange, NewContactFund } from "../contactChangesState";
import { getBaseChangeRequest, resolveSubmittedComment } from "../membersChangeRequestHelper";

export type NewContactStep = "contactDetails" | "fundAccess" | "permissions" | "review";

export const newContactSteps: NewContactStep[] = ["contactDetails", "fundAccess", "permissions", "review"];

export const newContactStepLabels: Record<NewContactStep, string> = {
  contactDetails: "Contact Details",
  fundAccess: "Fund Access",
  permissions: "Permissions",
  review: "Review",
};

export type NewContactField = "fullName" | "email";

interface NewContactFormValues {
  fullName: string;
  email: string;
}

interface NewContactFormErrors {
  fullName?: string;
  email?: string;
}

export interface NewContactEntry {
  key: number;
  values: NewContactFormValues;
  touchedFields: NewContactField[];
  errors: NewContactFormErrors;
}

export interface NewContactState {
  step: NewContactStep;
  isNextStepDisabled: boolean;
  contacts: NewContactEntry[];
  selectedFundIds: string[];
  role: InvestorPortalRole;
  selectedCategoryExternalIds: string[];
  comment?: string;
}

let nextContactEntryKey = 0;

const createEmptyContactEntry = (): NewContactEntry => ({
  key: nextContactEntryKey++,
  values: { fullName: "", email: "" },
  touchedFields: [],
  errors: {},
});

const getOtherEmails = (contacts: NewContactEntry[], contactIndex: number): string[] =>
  contacts.filter((_, index) => index !== contactIndex).map((contact) => contact.values.email);

const duplicateEmailValidator = (otherEmails: string[]): Validator<string> => {
  const validator = uniqueValidator(
    "Contact with this email is already added",
    otherEmails.map((email) => email.trim().toLowerCase())
  );
  return (value) => validator(value.toLowerCase());
};

const validateContactEntry = (values: NewContactFormValues, otherEmails: string[]): NewContactFormErrors => ({
  fullName: combineValidators(requiredValidator, maxCharactersValidator(100))(values.fullName).error,
  email: combineValidators(requiredValidator, emailValidator, duplicateEmailValidator(otherEmails))(values.email).error,
});

const isContactEntryValid = (contacts: NewContactEntry[], contactIndex: number, entry: NewContactEntry): boolean =>
  Object.values(validateContactEntry(entry.values, getOtherEmails(contacts, contactIndex))).every((error) => !error);

const isStepValid = (state: NewContactState, step: NewContactStep): boolean => {
  switch (step) {
    case "contactDetails":
      return (
        state.contacts.length > 0 &&
        state.contacts.every((entry, index) => isContactEntryValid(state.contacts, index, entry))
      );
    case "fundAccess":
      return state.selectedFundIds.length > 0;
    case "permissions":
      return state.selectedCategoryExternalIds.length > 0;
    default:
      return true;
  }
};

const withVisibleErrors = (contacts: NewContactEntry[]): NewContactEntry[] =>
  contacts.map((entry, index) => {
    const allErrors = validateContactEntry(entry.values, getOtherEmails(contacts, index));
    const errors = Object.fromEntries(
      Object.entries(allErrors).filter(
        ([key, error]) => !!error && entry.touchedFields.includes(key as NewContactField)
      )
    );
    return { ...entry, errors };
  });

const withNextStepDisabled = (state: NewContactState): NewContactState => ({
  ...state,
  isNextStepDisabled: !isStepValid(state, state.step),
});

export const getInitialNewContactState = (): NewContactState => ({
  step: "contactDetails",
  isNextStepDisabled: true,
  contacts: [createEmptyContactEntry()],
  selectedFundIds: [],
  role: "Member",
  selectedCategoryExternalIds: [],
});

export const getContactsCountLabel = (state: NewContactState): string =>
  `${state.contacts.length} ${state.contacts.length === 1 ? "contact" : "contacts"}`;

export const getSelectedFundsCountLabel = (state: NewContactState): string =>
  `${state.selectedFundIds.length} ${state.selectedFundIds.length === 1 ? "fund" : "funds"}`;

export const getManageableFunds = (contacts: FundInvestorContact[]): Fund[] =>
  distinctBy(
    contacts.filter((contact) => !contact.isCurrentUser || contact.role === "Admin"),
    (contact) => contact.fundId
  ).map((contact) => ({ id: contact.fundId, name: contact.fundName }));

export const stateToUpsertMembersChangeRequestRequest = (
  state: NewContactState,
  fundInvestors: InvestorFundPermissionSet[],
  latestChangeRequest: MembersChangeRequestView | undefined,
  submissionId: string
): UpsertMembersChangeRequestRequest => {
  const fundPermissions: MemberFundPermissions[] = state.selectedFundIds
    .map((fundId) => fundInvestors.find((fundInvestor) => fundInvestor.fundId === fundId)?.fundInvestorId)
    .filter(isDefined())
    .map((fundInvestorId) => ({
      fundInvestorId,
      role: state.role,
      categories: state.selectedCategoryExternalIds,
    }));

  return {
    ...getBaseChangeRequest(latestChangeRequest),
    upsertItems: state.contacts.map((contact) => ({
      action: "CreateNew",
      name: contact.values.fullName.trim(),
      email: contact.values.email.trim(),
      fundPermissions,
    })),
    commentUserInput: resolveSubmittedComment(state.comment, latestChangeRequest),
    submissionId,
  };
};

export const stateToNewContactChanges = (
  state: NewContactState,
  fundInvestors: InvestorFundPermissionSet[]
): NewContactChange[] => {
  const funds: NewContactFund[] = state.selectedFundIds
    .map((fundId) => fundInvestors.find((fundInvestor) => fundInvestor.fundId === fundId))
    .filter(isDefined())
    .map((fundInvestor) => ({
      fundId: fundInvestor.fundId,
      fundName: fundInvestor.fundName,
      fundInvestorId: fundInvestor.fundInvestorId,
    }));

  return state.contacts.map((contact) => {
    const email = contact.values.email.trim();
    return {
      // The synthetic id groups the pending contact's grid rows and stays stable across re-adds of the same email
      contactId: `new-contact-${email.toLowerCase()}`,
      name: contact.values.fullName.trim(),
      email,
      role: state.role,
      categoryExternalIds: state.selectedCategoryExternalIds,
      funds,
    };
  });
};

// Actions

export const nextStepAction = (): StateAction<NewContactState> => (state) => {
  const nextStep = newContactSteps[newContactSteps.indexOf(state.step) + 1];
  if (nextStep === undefined) {
    return state;
  }
  return withNextStepDisabled({ ...state, step: nextStep });
};

export const previousStepAction = (): StateAction<NewContactState> => (state) => {
  const previousStep = newContactSteps[newContactSteps.indexOf(state.step) - 1];
  if (previousStep === undefined) {
    return state;
  }
  return withNextStepDisabled({ ...state, step: previousStep });
};

export const updateContactFieldAction =
  (contactIndex: number, field: NewContactField, value: string): StateAction<NewContactState> =>
  (state) => {
    const entry = state.contacts[contactIndex];
    if (entry === undefined) {
      return state;
    }

    const contacts = state.contacts.map((contact, index) =>
      index === contactIndex
        ? {
            ...contact,
            values: { ...contact.values, [field]: value },
            touchedFields: distinct([...contact.touchedFields, field]),
          }
        : contact
    );

    return withNextStepDisabled({ ...state, contacts: withVisibleErrors(contacts) });
  };

export const addContactAction = (): StateAction<NewContactState> => (state) =>
  withNextStepDisabled({ ...state, contacts: withVisibleErrors([...state.contacts, createEmptyContactEntry()]) });

export const removeContactAction =
  (contactIndex: number): StateAction<NewContactState> =>
  (state) => {
    if (state.contacts.length <= 1) {
      return state;
    }
    return withNextStepDisabled({
      ...state,
      contacts: withVisibleErrors(state.contacts.filter((_, index) => index !== contactIndex)),
    });
  };

export const toggleFundSelectionAction =
  (fundId: string): StateAction<NewContactState> =>
  (state) => {
    const selectedFundIds = state.selectedFundIds.includes(fundId)
      ? state.selectedFundIds.filter((id) => id !== fundId)
      : [...state.selectedFundIds, fundId];

    return withNextStepDisabled({ ...state, selectedFundIds });
  };

export const updateRoleAction =
  (role: InvestorPortalRole): StateAction<NewContactState> =>
  (state) => ({ ...state, role });

export const toggleCategorySelectionAction =
  (categoryExternalId: string): StateAction<NewContactState> =>
  (state) => {
    const selectedCategoryExternalIds = state.selectedCategoryExternalIds.includes(categoryExternalId)
      ? state.selectedCategoryExternalIds.filter((id) => id !== categoryExternalId)
      : [...state.selectedCategoryExternalIds, categoryExternalId];

    return withNextStepDisabled({ ...state, selectedCategoryExternalIds });
  };

export const setAllCategoriesSelectionAction =
  (allCategoryExternalIds: string[], selected: boolean): StateAction<NewContactState> =>
  (state) =>
    withNextStepDisabled({ ...state, selectedCategoryExternalIds: selected ? allCategoryExternalIds : [] });

export const updateCommentAction =
  (value: string): StateAction<NewContactState> =>
  (state) => ({ ...state, comment: value });
